import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ChevronDown, ChevronRight, Heart, Menu, ShoppingCart, User, X, LucideIconData } from 'lucide-angular';
import { catchError, map, of } from 'rxjs';
import { SearchBarComponent } from '../../shared/ui/search-bar/search-bar.component';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { BadgeComponent } from '../../shared/ui/badge/badge.component';
import { PpCoinComponent } from '../../shared/ui/pp-coin/pp-coin.component';
import { AuthService } from '../../shared/services/auth.service';
import { AuthModalService } from '../../shared/services/auth-modal.service';
import { CartDrawerService } from '../../shared/services/cart-drawer.service';
import { CartService } from '../../shared/services/cart.service';
import { ToastService } from '../../shared/services/toast.service';
import { CategoryService } from '../../shared/services/category.service';
import { PrepurchaseService } from '../../shared/services/prepurchase.service';
import { PointsService } from '../../shared/services/points.service';
import { PerronPointsModalService } from '../../shared/services/perron-points-modal.service';
import {
  PERRON_POINTS_COPY,
  withMissing,
  withPoints,
} from '../../shared/perron-points/perron-points-copy';
import { SearchOverlayService } from '../../features/search/data/search-overlay.service';
import { PerronCoinsSettingsService } from '../../features/perron-coins/data/perron-coins-settings.service';
import { opportunitiesEnabled } from '../../features/opportunities/data/opportunities-flag';
import { OPPORTUNITIES_COPY } from '../../features/opportunities/opportunities.copy';
import { rafflesEnabled } from '../../features/raffles/data/raffles-flag';
import { RAFFLES_COPY } from '../../features/raffles/raffles.copy';
import { PROFILE_COPY } from '../../features/profile/profile.copy';
import { Category } from '../../shared/models/category.model';
import { Product } from '../../shared/models/product.model';
import { SubcategoryItem } from '../../features/catalog/data/catalog.models';
import {
  resolvePdpCategorySlug,
  toSubcategoryItems,
} from '../../features/catalog/data/catalog-mappers';
import { CatalogService } from '../../features/catalog/data/catalog.service';
import { GameSectionService } from '../../features/game-section/data/game-section.service';
import { publicGameSummaryToUi } from '../../features/game-section/data/game-section.mappers';
import { GameSectionSummary } from '../../features/game-section/models/game-section.model';
import { calcDiscountPercent, formatPrice, toUiProduct } from '../../shared/utils/product.utils';
import { userAvatarUrl } from '../../shared/utils/user-avatar.util';
import {
  MegaRecentProduct,
  TIENDA_QUICK_LINKS,
} from './data/mega-tienda.data';

interface MainNavItem {
  label: string;
  route: string | null;
  hasMega: boolean;
  megaKey: string | null;
  accent?: boolean;
}

// TODO: mover a servicio/config cuando los menús vengan del API
const MAIN_NAV_ITEMS: MainNavItem[] = [
  { label: 'Tienda',     route: '/tienda',             hasMega: true,  megaKey: 'tienda' },
  { label: 'Juegos',     route: null,                  hasMega: true,  megaKey: 'juegos' },
  { label: 'Precompras', route: '/tienda/precompras', hasMega: false, megaKey: null, accent: true },
  { label: 'Eventos',    route: '/eventos',            hasMega: false, megaKey: null     },
  { label: 'Comunidad',  route: '/comunidad',          hasMega: false, megaKey: null     },
  { label: 'Nosotros',   route: '/nosotros',           hasMega: false, megaKey: null     },
  { label: 'Contacto',   route: '/contacto',           hasMega: false, megaKey: null     },
];

/** Rifas (B1·web, W4): solo con el flag `raffles` (D59), entre «Eventos» y «Comunidad». */
const RAFFLES_NAV_ITEM: MainNavItem = { label: RAFFLES_COPY.NAV_LINK, route: '/rifas', hasMega: false, megaKey: null };
/** Oportunidades (B2·web, D121): solo con el flag `opportunities` (D123), tras «Rifas» si sale o, si no, tras «Eventos». */
const OPPORTUNITIES_NAV_ITEM: MainNavItem = {
  label: OPPORTUNITIES_COPY.NAV_LINK,
  route: '/oportunidades',
  hasMega: false,
  megaKey: null,
};
const COINS_NAV_AFTER = '/eventos';

/** `MAIN_NAV_ITEMS` con «Rifas» y «Oportunidades» tras «Eventos» si toca, en ese orden; los demás items no cambian. */
function withCoinsItems(items: readonly MainNavItem[], raffles: boolean, opportunities: boolean): MainNavItem[] {
  const extra = [...(raffles ? [RAFFLES_NAV_ITEM] : []), ...(opportunities ? [OPPORTUNITIES_NAV_ITEM] : [])];
  if (!extra.length) return [...items];
  return items.flatMap((item) => (item.route === COINS_NAV_AFTER ? [item, ...extra] : [item]));
}

const RECENT_PRODUCTS_LIMIT = 6;
const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';
// Nombre del root catch-all del backend. Lo detectamos por nombre (no por id)
// para ser estable entre envs. Hovereárlo o clickearlo se trata como "todos".
const ROOT_CATEGORY_NAME = 'Tienda';

// ViewModel local del megamenú: el template usa estos nombres y rutas resueltas,
// así el HTML no depende del shape `Category` del backend.
interface MegaCategoryVM {
  id: number;
  name: string;
  route: string;
  description: string;
  subcategories: SubcategoryItem[];
  synthetic?: boolean;
}

// Id sintético del item "Precompras" en el megamenú de Tienda — pseudo-categoría
// que no existe en el backend pero se expone al usuario como si lo fuera.
// Usamos -1 para no colisionar con ids reales (el backend usa unsigned ints).
const PREPURCHASE_SYNTHETIC_ID = -1;

// Estado discriminado para los grids de empty-state del mega. Diferencia
// "cargando inicial" de "cargó vacío" y de "falló la request", que antes
// colapsaban a un único `[]` y dejaban la UI pegada en "Cargando novedades...".
type MegaLoadState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; value: T }
  | { status: 'error' };

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    SearchBarComponent,
    IconComponent,
    BadgeComponent,
    PpCoinComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly searchOverlay = inject(SearchOverlayService);
  private readonly router = inject(Router);

  // Flags de rifas y oportunidades: mismo criterio que `/rifas` y `/oportunidades` (`rafflesEnabled`,
  // `opportunitiesEnabled`). Ajustes en `null` o aún sin llegar → sin enlaces.
  // Sin gate de plataforma: el enlace sale en el HTML del SSR y el TransferState del servicio evita el refetch.
  private readonly perronCoinsSettings = toSignal(inject(PerronCoinsSettingsService).get(), { initialValue: null });

  protected readonly mainNavItems = computed(() => {
    const settings = this.perronCoinsSettings();
    return withCoinsItems(MAIN_NAV_ITEMS, rafflesEnabled(settings), opportunitiesEnabled(settings));
  });

  protected isMenuOpen = signal(false);
  protected activeMega = signal<string | null>(null);
  protected hoveredCategory = signal<number | null>(null);
  protected expandedDrawerSection = signal<string | null>(null);

  protected readonly tiendaQuickLinks = TIENDA_QUICK_LINKS;

  private readonly categoryService = inject(CategoryService);
  private readonly catalogService = inject(CatalogService);
  private readonly prepurchaseService = inject(PrepurchaseService);
  private readonly gameSectionService = inject(GameSectionService);

  // Gate de plataforma: las cargas que sólo alimentan el contenido del
  // megamenú (no visible en first paint) se evitan en SSR para no inflar
  // el HTML inicial vía TransferState. Sólo `rootsSig` se mantiene en SSR
  // porque sus enlaces son críticos para el crawler.
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Dos caches paralelos. El primero (con `children`) alimenta la lista de
  // categorías + subcategorías; el segundo (con `last_updated_products`) sólo
  // se usa para la columna de productos recientes cuando hay categoría hovered.
  private readonly rootsSig = toSignal(
    this.categoryService.getRootCategoriesCached(),
    { initialValue: [] as Category[] },
  );
  // Índice de secciones de juego para el megamenú "Juegos". SIN gate de
  // plataforma, por la misma razón que `rootsSig`: son 8 filas ligeras y los
  // enlaces /juegos/:slug son críticos para el crawler, así que deben salir en
  // el HTML del SSR. El TransferState del servicio evita el refetch al hidratar.
  // Un fallo de red degrada a lista vacía → la entrada del nav no se pinta.
  private readonly gamesSig = toSignal(
    this.gameSectionService.getGames().pipe(
      map((rows) => rows.map(publicGameSummaryToUi)),
      catchError((err) => {
        console.error('[header/mega] games index fetch failed', err);
        return of([] as GameSectionSummary[]);
      }),
    ),
    { initialValue: [] as GameSectionSummary[] },
  );

  protected readonly games = this.gamesSig;

  // Sin juegos publicados (o con el índice caído) no hay nada que enseñar: la
  // entrada "Juegos" desaparece del nav y del drawer en vez de dejar un
  // trigger que abre un panel vacío.
  protected readonly showGamesNav = computed(() => this.gamesSig().length > 0);

  // Sólo cliente: el árbol con `last_updated_products` pesa megabytes y sólo
  // se usa al hacer hover dentro del megamenú abierto. Mantenerlo fuera de
  // TransferState reduce el HTML inicial drásticamente.
  private readonly rootsWithRecentSig = toSignal(
    this.isBrowser
      ? this.categoryService.getRootCategoriesWithRecentCached()
      : of([] as Category[]),
    { initialValue: [] as Category[] },
  );

  // Empty state del mega (sin hover): 6 populares + 6 recientes globales.
  // Sólo cliente: el mega no es visible en first paint, así que serializar
  // estos productos en TransferState es payload muerto. El browser hace los
  // fetches reales tras la hidratación; mientras tanto, las grids muestran
  // el placeholder de loading que ya soporta el template.
  private readonly popularState = toSignal(
    this.isBrowser
      ? this.loadMegaProducts('popular')
      : of<MegaLoadState<Product[]>>({ status: 'loading' }),
    { initialValue: { status: 'loading' } as MegaLoadState<Product[]> },
  );

  private readonly globalRecentState = toSignal(
    this.isBrowser
      ? this.loadMegaProducts('newest')
      : of<MegaLoadState<Product[]>>({ status: 'loading' }),
    { initialValue: { status: 'loading' } as MegaLoadState<Product[]> },
  );

  // Productos en precompra — usados tanto por el item sintético del megamenú
  // "Tienda" como por la preview cuando el user hace hover sobre "Precompras".
  // Sólo cliente por la misma razón: invisibles en first paint.
  private readonly prepurchaseState = toSignal(
    this.isBrowser
      ? this.prepurchaseService.getPrepurchaseProducts().pipe(
          map((value): MegaLoadState<Product[]> => ({ status: 'loaded', value })),
          catchError((err) => {
            console.error('[header/mega] prepurchase fetch failed', err);
            return of<MegaLoadState<Product[]>>({ status: 'error' });
          }),
        )
      : of<MegaLoadState<Product[]>>({ status: 'loading' }),
    { initialValue: { status: 'loading' } as MegaLoadState<Product[]> },
  );

  private loadMegaProducts(sort: 'popular' | 'newest') {
    return this.catalogService
      .getCatalogData({
        category: null,
        filters: {},
        search: '',
        sort,
        page: 1,
        perPage: RECENT_PRODUCTS_LIMIT,
      })
      .pipe(
        map((r): MegaLoadState<Product[]> => ({ status: 'loaded', value: r.products })),
        catchError((err) => {
          console.error(`[header/mega] ${sort} fetch failed`, err);
          return of<MegaLoadState<Product[]>>({ status: 'error' });
        }),
      );
  }

  protected readonly tiendaCategories = computed<MegaCategoryVM[]>(() => {
    const real: MegaCategoryVM[] = this.rootsSig().map((cat) => ({
      id: cat.id,
      name: cat.name,
      // El root catch-all va a /tienda plano, no /tienda/tienda.
      route: cat.name === ROOT_CATEGORY_NAME ? '/tienda' : `/tienda/${cat.slug}`,
      description: cat.description ?? '',
      subcategories: toSubcategoryItems(cat.children),
    }));

    // Item sintético "Precompras" al final de la lista de categorías. No viene
    // del backend — lo inyectamos en cliente para que aparezca en el megamenú
    // con el mismo comportamiento hover que cualquier otra categoría.
    real.push({
      id: PREPURCHASE_SYNTHETIC_ID,
      name: 'Precompras',
      route: '/tienda/precompras',
      description: 'Reserva productos antes de su lanzamiento con un 30% de abono.',
      subcategories: [],
      synthetic: true,
    });

    return real;
  });

  // True cuando el hover cae sobre el root Tienda: queremos que se comporte
  // como "sin selección" para que las grids globales ocupen el espacio.
  private readonly hoveredIsRoot = computed(() => {
    const id = this.hoveredCategory();
    if (id === null) return false;
    return this.rootsSig().find((c) => c.id === id)?.name === ROOT_CATEGORY_NAME;
  });

  // Señal de visibilidad para las grids de empty state: se muestran cuando
  // nada está hovered O cuando el hover es el root Tienda.
  protected readonly showGlobalGrids = computed(
    () => this.hoveredCategory() === null || this.hoveredIsRoot(),
  );

  protected readonly activeCategoryDetail = computed(() => {
    const id = this.hoveredCategory();
    if (id === null || this.hoveredIsRoot()) return null;
    return this.tiendaCategories().find((c) => c.id === id) ?? null;
  });

  protected readonly recentProductsForHovered = computed<MegaRecentProduct[]>(() => {
    const id = this.hoveredCategory();
    if (id === null || this.hoveredIsRoot()) return [];
    // Caso especial: item sintético "Precompras" → listado de precompras reales.
    if (id === PREPURCHASE_SYNTHETIC_ID) return this.prepurchaseRecentProducts();
    const cat = this.rootsWithRecentSig().find((c) => c.id === id);
    const products = cat?.last_updated_products ?? [];
    const categorySlug = cat?.slug ?? '';
    const roots = this.rootsSig();
    return products
      .slice(0, RECENT_PRODUCTS_LIMIT)
      .map((p) => toMegaRecentProduct(p, roots, categorySlug))
      .filter((vm): vm is MegaRecentProduct => vm !== null);
  });

  protected readonly popularStatus = computed(() => this.popularState().status);
  protected readonly globalRecentStatus = computed(() => this.globalRecentState().status);
  protected readonly prepurchaseStatus = computed(() => this.prepurchaseState().status);

  /** True cuando el item sintético "Precompras" está hovered en el megamenú. */
  protected readonly isPrepurchaseHovered = computed(
    () => this.hoveredCategory() === PREPURCHASE_SYNTHETIC_ID,
  );

  protected readonly prepurchaseRecentProducts = computed<MegaRecentProduct[]>(() => {
    const state = this.prepurchaseState();
    if (state.status !== 'loaded') return [];
    const roots = this.rootsSig();
    return state.value
      .slice(0, RECENT_PRODUCTS_LIMIT)
      .map((p) => toMegaRecentProduct(p, roots))
      .filter((vm): vm is MegaRecentProduct => vm !== null);
  });

  protected readonly popularProducts = computed<MegaRecentProduct[]>(() => {
    const state = this.popularState();
    if (state.status !== 'loaded') return [];
    const roots = this.rootsSig();
    return state.value
      .map((p) => toMegaRecentProduct(p, roots))
      .filter((vm): vm is MegaRecentProduct => vm !== null);
  });

  protected readonly globalRecentProducts = computed<MegaRecentProduct[]>(() => {
    const state = this.globalRecentState();
    if (state.status !== 'loaded') return [];
    const roots = this.rootsSig();
    return state.value
      .map((p) => toMegaRecentProduct(p, roots))
      .filter((vm): vm is MegaRecentProduct => vm !== null);
  });

  protected readonly cartCount = inject(CartService).cartCount;

  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly toastService = inject(ToastService);
  private readonly pointsService = inject(PointsService);
  private readonly perronPointsModalService = inject(PerronPointsModalService);

  protected isUserMenuOpen = signal(false);
  protected readonly isLoggedIn = this.authService.isAuthenticated;
  protected readonly currentUser = this.authService.currentUser;
  /** URL del avatar que falló al cargar: el botón vuelve al icono (un avatar nuevo se vuelve a intentar). */
  private readonly failedUserAvatar = signal<string | null>(null);
  /** `avatar` es solo el nombre del fichero (N11): sin resolverlo, el `src` daba una imagen rota. */
  protected readonly userAvatar = computed(() => {
    const url = userAvatarUrl(this.currentUser()?.avatar);
    return url && url !== this.failedUserAvatar() ? url : null;
  });
  protected readonly profileCopy = PROFILE_COPY;

  protected readonly ppCopy = PERRON_POINTS_COPY.header;
  protected readonly withPoints = withPoints;
  protected readonly withMissing = withMissing;
  protected readonly pointsBalance = this.pointsService.balance;
  protected readonly pointsEarlyAccess = this.pointsService.earlyAccess;
  protected readonly pointsThreshold = this.pointsService.threshold;
  protected readonly pointsMissing = this.pointsService.missingForThreshold;
  protected readonly pointsLoading = this.pointsService.loading;
  protected readonly pointsLoadError = this.pointsService.loadError;

  protected readonly menuIcon: LucideIconData = Menu;
  protected readonly closeIcon: LucideIconData = X;
  protected readonly chevronIcon: LucideIconData = ChevronDown;
  protected readonly userIcon: LucideIconData = User;
  protected readonly cartIcon: LucideIconData = ShoppingCart;
  protected readonly wishlistIcon: LucideIconData = Heart;
  protected readonly chevronRightIcon: LucideIconData = ChevronRight;

  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const handler = (e: MouseEvent) => {
        if (!this.elementRef.nativeElement.contains(e.target as Node)) {
          this.isMenuOpen.set(false);
          this.closeMega();
          this.closeUserMenu();
        }
      };
      this.document.addEventListener('click', handler);
      this.destroyRef.onDestroy(() => this.document.removeEventListener('click', handler));
    });
  }

  protected toggleMenu(): void {
    this.isMenuOpen.update(v => !v);
  }

  /**
   * Click en el logo. Normalmente el `routerLink="/"` navega al home, pero el
   * Router de Angular hace no-op si YA estás en el home, lo que el usuario
   * percibe como "el logo no hace nada". Para esos casos forzamos un reload
   * completo (equivalente a F5) en vez de dejar pasar el click muerto.
   * Respetamos ctrl/cmd/shift-click y el botón central para no romper el
   * "abrir en pestaña nueva".
   */
  protected onLogoClick(event: MouseEvent): void {
    this.closeMenu();
    this.closeUserMenu();

    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    // Ruta actual sin query ni fragment: sólo nos interesa el path.
    const path = this.router.url.split(/[?#]/)[0];
    if (path === '/') {
      event.preventDefault();
      this.document.defaultView?.location.reload();
    }
  }

  /**
   * Corazón de la cabecera. Con sesión es un `<a routerLink="/perfil/deseos">` y aquí solo se cierran los
   * menús; sin sesión es un botón que abre el login (sin toast).
   */
  protected onWishlistClick(): void {
    this.closeUserMenu();
    this.closeMega();
    if (!this.isLoggedIn()) this.authModalService.open('login');
  }

  protected onUserAvatarError(): void {
    this.failedUserAvatar.set(this.userAvatar());
  }

  protected closeMenu(): void {
    this.isMenuOpen.set(false);
    this.closeMega();
    this.expandedDrawerSection.set(null);
  }

  protected toggleDrawerSection(key: string): void {
    this.expandedDrawerSection.update(current => current === key ? null : key);
  }

  protected openMega(key: string): void {
    this.activeMega.set(key);
  }

  protected closeMega(): void {
    this.activeMega.set(null);
    this.hoveredCategory.set(null);
  }

  protected toggleMega(key: string): void {
    this.activeMega() === key ? this.closeMega() : this.openMega(key);
  }

  protected toggleUserMenu(): void {
    this.isUserMenuOpen.update(v => !v);
    if (this.isUserMenuOpen()) this.closeMega();
  }

  protected closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  protected onLoginClick(): void {
    this.closeUserMenu();
    this.authModalService.open('login');
  }

  protected onRegisterClick(): void {
    this.closeUserMenu();
    this.authModalService.open('register');
  }

  protected onLogout(): void {
    this.closeUserMenu();
    this.authService.logout();
    this.toastService.show({ type: 'success', message: 'Sesión cerrada.' });
  }

  protected onOpenPerronPointsModal(): void {
    this.closeUserMenu();
    this.perronPointsModalService.open();
  }

  protected onCartClick(): void {
    this.closeUserMenu();
    this.closeMega();
    this.cartDrawerService.open();
  }

  /** Cada tecla — estado UI sincrónico (mostrar/ocultar recents al instante). */
  protected onSearchInput(query: string): void {
    this.searchOverlay.setQuery(query);
  }

  /** Debounced (400ms) — dispara el fetch al API via switchMap. */
  protected onSearchDebounced(query: string): void {
    this.searchOverlay.submitQuery(query);
  }

  /**
   * Enter o click en el botón de lupa. Si hay una fila resaltada con ↑↓,
   * navega al producto; si no, commit hacia /buscar con la query. `setQuery`
   * asegura que el signal esté al día por si el usuario pulsó Enter antes
   * de que dispare el debounce de 400ms.
   */
  protected onSearchCommit(query: string): void {
    this.closeMenu();
    this.searchOverlay.setQuery(query);
    this.searchOverlay.selectHighlighted();
  }

  protected onSearchFocus(): void {
    this.searchOverlay.open();
  }

  protected onSearchBlur(): void {
    // En mobile el overlay es un sheet a pantalla completa con su propio input;
    // se cierra vía backdrop, botón X o Esc. El blur del search-bar del header
    // NO debe cerrarlo: al enfocar el input del sheet, el del header pierde foco
    // y eso cerraría el sheet apenas se abre.
    if (this.searchOverlay.isMobile()) return;
    // En desktop el dropdown sí se cierra al salir el foco del input. Los clicks
    // dentro del overlay usan `mousedown.preventDefault()`, así que no disparan
    // blur — si el blur llega, es porque el foco salió a otro sitio real.
    this.searchOverlay.close();
  }

  protected fallbackImage(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && img.src !== FALLBACK_IMAGE_URL && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }
}

/**
 * Construye el VM del producto que muestra el megamenú. Si el caller tiene un
 * contexto explícito (p. ej. categoría hovered), lo pasa via `categorySlug`;
 * si no, derivamos el slug canónico desde `product.categories[0]`. Devuelve
 * `null` cuando no hay slug de categoría (producto sin categories) — el
 * caller filtra esos para no renderizar URLs rotas. En la práctica nunca
 * sucede porque el backend siempre embebe categorías con slug autoritativo.
 */
function toMegaRecentProduct(
  apiProduct: Product,
  roots: Category[],
  categorySlug?: string,
): MegaRecentProduct | null {
  // `toUiProduct` aplica impuestos, resuelve `imageUrl`, normaliza `isOnSale` y
  // calcula `originalPrice`. Antes leíamos `p.price` crudo → salía ₡0 para
  // productos con precio sólo en `sale_price` o con IVA no aplicado.
  const ui = toUiProduct(apiProduct);
  const slug = categorySlug || resolvePdpCategorySlug(ui, roots);
  if (!slug) return null;
  const discountPercent = ui.isOnSale ? calcDiscountPercent(ui) || undefined : undefined;
  // Solo confiamos en el flag explícito del backend. La heurística por
  // ventana de 30 días sobre `created_at` daba falsos positivos cuando una
  // carga masiva de productos hacía que todo apareciera como "Novedad".
  const isNew = apiProduct.isNew === true;
  const productSlug = ui.slug ?? String(ui.id);

  return {
    id: String(ui.id),
    name: ui.name,
    priceFormatted: formatPrice(ui),
    imageUrl: ui.imageUrl || FALLBACK_IMAGE_URL,
    route: `/tienda/${slug}/${productSlug}`,
    isNew: isNew || undefined,
    isOnSale: ui.isOnSale || undefined,
    discountPercent,
    isPreSale: ui.isPreSale || undefined,
  };
}
